import {
  DimensionSide,
  Element,
  ElementType,
  Polyline,
} from '@ritabygglov/core';
import { Coordinate } from '../../../../types';
import { computeTrapezoidVertices } from '../../../../services/Actions/Trapezoid';
import { computeTriangleVertices } from '../../../../helpers/triangleGeometry';
import { DimensionDragInfo } from '../../../../types';

const MIN_DIMENSION_OFFSET = 5;

const rotatePoint = (
  point: Coordinate,
  cx: number,
  cy: number,
  rotationRad: number
): Coordinate => {
  const cos = Math.cos(rotationRad);
  const sin = Math.sin(rotationRad);
  const dx = point.x - cx;
  const dy = point.y - cy;
  return { x: cx + dx * cos - dy * sin, y: cy + dx * sin + dy * cos };
};

type OffsetCalculation = {
  calculate: (
    element: Element,
    dragInfo: DimensionDragInfo,
    currentX: number,
    currentY: number
  ) => number;
  getStartOffset: (element: Element, dragInfo: DimensionDragInfo) => number;
  updateElement: (
    element: Element,
    newOffset: number,
    dragInfo: DimensionDragInfo
  ) => Element;
};

const getPerpendicularProjection = (
  startPoint: Coordinate,
  endPoint: Coordinate,
  mouseStartX: number,
  mouseStartY: number,
  mouseCurrentX: number,
  mouseCurrentY: number,
  dimensionSide: DimensionSide
): number => {
  const dx = endPoint.x - startPoint.x;
  const dy = endPoint.y - startPoint.y;

  const perpDx = -dy;
  const perpDy = dx;
  const perpLength = Math.sqrt(perpDx * perpDx + perpDy * perpDy);
  const normPerpDx = perpDx / perpLength;
  const normPerpDy = perpDy / perpLength;

  const mouseDx = mouseCurrentX - mouseStartX;
  const mouseDy = mouseCurrentY - mouseStartY;

  const projection = mouseDx * normPerpDx + mouseDy * normPerpDy;
  const directionMultiplier = dimensionSide === DimensionSide.Above ? 1 : -1;

  return projection * directionMultiplier;
};

const widthStrategy: OffsetCalculation = {
  calculate: (element, dragInfo, currentX, currentY) => {
    const rotationRad = (element.rotate * Math.PI) / 180;
    const perpDx = -Math.sin(rotationRad);
    const perpDy = Math.cos(rotationRad);
    const mouseDx = currentX - dragInfo.startMouseX;
    const mouseDy = currentY - dragInfo.startMouseY;
    const projection = mouseDx * perpDx + mouseDy * perpDy;
    return Math.max(MIN_DIMENSION_OFFSET, dragInfo.startOffset + projection);
  },

  getStartOffset: (element) => {
    if (
      element.type === ElementType.Rect ||
      element.type === ElementType.Triangle ||
      element.type === ElementType.Angle
    ) {
      return element.dimensionOffsetWidth ?? 15;
    }
    return 15;
  },

  updateElement: (element, newOffset) => ({
    ...element,
    dimensionOffsetWidth: newOffset,
  }),
};

const heightStrategy: OffsetCalculation = {
  calculate: (element, dragInfo, currentX, currentY) => {
    const rotationRad = (element.rotate * Math.PI) / 180;
    const edgeDx = -Math.sin(rotationRad);
    const edgeDy = Math.cos(rotationRad);
    const perpDx = -edgeDy;
    const perpDy = edgeDx;
    const mouseDx = currentX - dragInfo.startMouseX;
    const mouseDy = currentY - dragInfo.startMouseY;
    const projection = mouseDx * perpDx + mouseDy * perpDy;
    return Math.max(MIN_DIMENSION_OFFSET, dragInfo.startOffset + projection);
  },

  getStartOffset: (element) => {
    if (
      element.type === ElementType.Rect ||
      element.type === ElementType.Triangle ||
      element.type === ElementType.Angle ||
      element.type === ElementType.Trapezoid
    ) {
      return element.dimensionOffsetHeight ?? 15;
    }
    return 15;
  },

  updateElement: (element, newOffset) => ({
    ...element,
    dimensionOffsetHeight: newOffset,
  }),
};

const segmentStrategy: OffsetCalculation = {
  calculate: (element, dragInfo, currentX, currentY) => {
    if (element.type !== ElementType.Polyline) return dragInfo.startOffset;

    const polyline = element as Polyline;
    const segmentIndex = dragInfo.segmentIndex ?? 0;
    const point = polyline.points[segmentIndex];
    const nextPoint = polyline.points[segmentIndex + 1];

    if (!point || !nextPoint) return dragInfo.startOffset;

    const startPoint =
      point.connectingPointIndex !== undefined
        ? polyline.points[point.connectingPointIndex]
        : point;
    const endPoint =
      nextPoint.connectingPointIndex !== undefined
        ? polyline.points[nextPoint.connectingPointIndex]
        : nextPoint;

    const projectedDelta = getPerpendicularProjection(
      startPoint,
      endPoint,
      dragInfo.startMouseX,
      dragInfo.startMouseY,
      currentX,
      currentY,
      point.dimensionSide
    );

    return Math.max(
      MIN_DIMENSION_OFFSET,
      dragInfo.startOffset + projectedDelta
    );
  },

  getStartOffset: (element, dragInfo) => {
    if (element.type !== ElementType.Polyline) return 15;

    const polyline = element as Polyline;
    const segmentIndex = dragInfo.segmentIndex ?? 0;
    const defaultDistance = polyline.lineType === 'Dimension' ? 5 : 15;

    return polyline.points[segmentIndex]?.dimensionOffset ?? defaultDistance;
  },

  updateElement: (element, newOffset, dragInfo) => {
    if (element.type !== ElementType.Polyline) return element;

    const polyline = element as Polyline;
    const segmentIndex = dragInfo.segmentIndex ?? 0;
    const newPoints = [...polyline.points];

    newPoints[segmentIndex] = {
      ...newPoints[segmentIndex],
      dimensionOffset: newOffset,
    };

    return {
      ...polyline,
      points: newPoints,
    };
  },
};

/**
 * Triangles and angles place their apex the same way, so the leg endpoints are
 * computed once for both.
 */
const getApexShapeEdgeVertices = (
  element: Element,
  edge: 'left' | 'right'
): { start: Coordinate; end: Coordinate } | null => {
  if (
    element.type !== ElementType.Triangle &&
    element.type !== ElementType.Angle
  ) {
    return null;
  }

  const { apex, baseLeft, baseRight } = computeTriangleVertices(
    element.pointRatio,
    element.width,
    element.height
  );

  const cx = element.x + element.width / 2;
  const cy = element.y + element.height / 2;
  const rotationRad = (element.rotate * Math.PI) / 180;

  const apexAbs = rotatePoint(
    { x: element.x + apex.x, y: element.y + apex.y },
    cx,
    cy,
    rotationRad
  );
  const baseLeftAbs = rotatePoint(
    { x: element.x + baseLeft.x, y: element.y + baseLeft.y },
    cx,
    cy,
    rotationRad
  );
  const baseRightAbs = rotatePoint(
    { x: element.x + baseRight.x, y: element.y + baseRight.y },
    cx,
    cy,
    rotationRad
  );

  if (edge === 'left') {
    return { start: apexAbs, end: baseLeftAbs };
  }
  return { start: apexAbs, end: baseRightAbs };
};

const getTrapezoidEdgeVertices = (
  element: Element,
  edge: 'left' | 'right'
): { start: Coordinate; end: Coordinate } | null => {
  if (element.type !== ElementType.Trapezoid) return null;

  const computed = computeTrapezoidVertices(
    element.topWidth,
    element.bottomWidth,
    element.height
  );
  if (!computed) return null;

  const { vertices } = computed;
  const cx = element.x + element.width / 2;
  const cy = element.y + element.height / 2;
  const rotationRad = (element.rotate * Math.PI) / 180;

  const bottomLeft = rotatePoint(
    { x: element.x + vertices[0][0], y: element.y + vertices[0][1] },
    cx,
    cy,
    rotationRad
  );
  const bottomRight = rotatePoint(
    { x: element.x + vertices[1][0], y: element.y + vertices[1][1] },
    cx,
    cy,
    rotationRad
  );
  const topRight = rotatePoint(
    { x: element.x + vertices[2][0], y: element.y + vertices[2][1] },
    cx,
    cy,
    rotationRad
  );
  const topLeft = rotatePoint(
    { x: element.x + vertices[3][0], y: element.y + vertices[3][1] },
    cx,
    cy,
    rotationRad
  );

  if (edge === 'left') {
    return { start: topLeft, end: bottomLeft };
  }
  return { start: topRight, end: bottomRight };
};

const getEdgeVertices = (
  element: Element,
  edge: 'left' | 'right'
): { start: Coordinate; end: Coordinate } | null => {
  return (
    getApexShapeEdgeVertices(element, edge) ??
    getTrapezoidEdgeVertices(element, edge)
  );
};

const topWidthStrategy: OffsetCalculation = {
  calculate: (element, dragInfo, currentX, currentY) => {
    const rotationRad = (element.rotate * Math.PI) / 180;
    const perpDx = Math.sin(rotationRad);
    const perpDy = -Math.cos(rotationRad);
    const mouseDx = currentX - dragInfo.startMouseX;
    const mouseDy = currentY - dragInfo.startMouseY;
    const projection = mouseDx * perpDx + mouseDy * perpDy;
    return Math.max(MIN_DIMENSION_OFFSET, dragInfo.startOffset + projection);
  },

  getStartOffset: (element) => {
    if (element.type === ElementType.Trapezoid) {
      return element.dimensionOffsetTopWidth ?? 15;
    }
    return 15;
  },

  updateElement: (element, newOffset) => ({
    ...element,
    dimensionOffsetTopWidth: newOffset,
  }),
};

const bottomWidthStrategy: OffsetCalculation = {
  calculate: (element, dragInfo, currentX, currentY) => {
    const rotationRad = (element.rotate * Math.PI) / 180;
    const perpDx = -Math.sin(rotationRad);
    const perpDy = Math.cos(rotationRad);
    const mouseDx = currentX - dragInfo.startMouseX;
    const mouseDy = currentY - dragInfo.startMouseY;
    const projection = mouseDx * perpDx + mouseDy * perpDy;
    return Math.max(MIN_DIMENSION_OFFSET, dragInfo.startOffset + projection);
  },

  getStartOffset: (element) => {
    if (element.type === ElementType.Trapezoid) {
      return element.dimensionOffsetBottomWidth ?? 15;
    }
    return 15;
  },

  updateElement: (element, newOffset) => ({
    ...element,
    dimensionOffsetBottomWidth: newOffset,
  }),
};

const leftEdgeStrategy: OffsetCalculation = {
  calculate: (element, dragInfo, currentX, currentY) => {
    const vertices = getEdgeVertices(element, 'left');
    if (!vertices) return dragInfo.startOffset;

    const projectedDelta = getPerpendicularProjection(
      vertices.start,
      vertices.end,
      dragInfo.startMouseX,
      dragInfo.startMouseY,
      currentX,
      currentY,
      DimensionSide.Above
    );

    return Math.max(
      MIN_DIMENSION_OFFSET,
      dragInfo.startOffset + projectedDelta
    );
  },

  getStartOffset: (element) => {
    if (
      element.type === ElementType.Triangle ||
      element.type === ElementType.Angle ||
      element.type === ElementType.Trapezoid
    ) {
      return element.dimensionOffsetLeftEdge ?? 15;
    }
    return 15;
  },

  updateElement: (element, newOffset) => ({
    ...element,
    dimensionOffsetLeftEdge: newOffset,
  }),
};

const rightEdgeStrategy: OffsetCalculation = {
  calculate: (element, dragInfo, currentX, currentY) => {
    const vertices = getEdgeVertices(element, 'right');
    if (!vertices) return dragInfo.startOffset;

    const projectedDelta = getPerpendicularProjection(
      vertices.start,
      vertices.end,
      dragInfo.startMouseX,
      dragInfo.startMouseY,
      currentX,
      currentY,
      DimensionSide.Below
    );

    return Math.max(
      MIN_DIMENSION_OFFSET,
      dragInfo.startOffset + projectedDelta
    );
  },

  getStartOffset: (element) => {
    if (
      element.type === ElementType.Triangle ||
      element.type === ElementType.Angle ||
      element.type === ElementType.Trapezoid
    ) {
      return element.dimensionOffsetRightEdge ?? 15;
    }
    return 15;
  },

  updateElement: (element, newOffset) => ({
    ...element,
    dimensionOffsetRightEdge: newOffset,
  }),
};

export const dimensionStrategies: Record<string, OffsetCalculation> = {
  width: widthStrategy,
  height: heightStrategy,
  segment: segmentStrategy,
  'left-edge': leftEdgeStrategy,
  'right-edge': rightEdgeStrategy,
  'top-width': topWidthStrategy,
  'bottom-width': bottomWidthStrategy,
};
